import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

const PAGE_SIZES = [10, 20, 50, 100];

/**
 * Server-side pagination.
 *
 * `page` is zero-based, matching the API's `admin/orders/{size}/{page}`. The
 * original displayed a one-based antd pager over the same zero-based value and
 * converted at three separate call sites, one of which compared the wrong one
 * and silently swallowed a page change.
 */
export function Pagination({
  page,
  size,
  total,
  onPageChange,
  onSizeChange,
  className,
}: {
  page: number;
  size: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Omit where the page size is fixed; the selector is then not rendered. */
  onSizeChange?: (size: number) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const pageCount = Math.max(1, Math.ceil(total / size));
  const first = total === 0 ? 0 : page * size + 1;
  const last = Math.min(total, (page + 1) * size);

  return (
    <div
      className={cn(
        'flex flex-col-reverse items-center justify-between gap-3 sm:flex-row',
        className
      )}
    >
      <p className="text-muted-foreground text-xs tabular-nums">
        {t('pagination.showing', {
          first: formatNumber(first),
          last: formatNumber(last),
          total: formatNumber(total),
        })}
      </p>

      <div className="flex items-center gap-2">
        {onSizeChange && (
          <Select
            value={String(size)}
            onValueChange={(value) => onSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[4.5rem]" aria-label={t('pagination.pageSize')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((value) => (
                <SelectItem key={value} value={String(value)}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('pagination.previous')}
            disabled={page <= 0}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-muted-foreground px-2 text-xs tabular-nums whitespace-nowrap">
            {t('pagination.pageOf', { page: page + 1, pages: pageCount })}
          </span>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('pagination.next')}
            disabled={page + 1 >= pageCount}
            onClick={() => onPageChange(page + 1)}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
